import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import api from '../api/api';
import ClientForm from '../components/ClientForm';
import { usePermissions } from '../hooks/usePermissions';
import ExportModal from '../components/ExportModal';
import TreasuryAccountSelector from '../components/TreasuryAccountSelector';
import { Button, Badge } from '../components/ui';
import { showConfirm } from '../utils/confirmUtils';
import { toastSuccess, toastError, toastInfo } from '../utils/toasts';
import {
  IconPlus, IconPrinter, IconEdit, IconTrash, IconX,
  IconFileExport, IconCurrencyDollar,
  IconFileInvoice, IconHandClick, IconReceipt, IconPercentage,
  IconChevronLeft, IconChevronRight,
} from '@tabler/icons-react';

const getObjectIdString = (ref) => {
  if (!ref) return null;
  if (typeof ref === 'string') return ref;
  if (ref._id) return ref._id.toString();
  if (ref.toString && typeof ref.toString === 'function') {
    const str = ref.toString();
    if (str !== '[object Object]' && str.length === 24) return str;
  }
  return null;
};

const getInvoiceNumber = (selectedInvoice, invoicesList = []) => {
  if (!selectedInvoice) return 'N/A';
  if (typeof selectedInvoice === 'object' && selectedInvoice.number) return selectedInvoice.number;
  if (typeof selectedInvoice === 'string') {
    const inv = invoicesList.find(i => i._id === selectedInvoice);
    return inv?.number || 'N/A';
  }
  if (typeof selectedInvoice === 'object' && selectedInvoice._id) {
    const invId = selectedInvoice._id.toString();
    const inv = invoicesList.find(i => i._id === invId);
    return inv?.number || 'N/A';
  }
  return 'N/A';
};

const getInvoiceTotal = (invoice) => {
  if (!invoice) return 0;
  return parseFloat(invoice.totalTTC) || parseFloat(invoice.total) || 0;
};

const PAYMENT_METHOD_LABELS = {
  cash: 'Espèces', check: 'Chèque', bank_transfer: 'Virement',
  draft: 'Traite', withholding_tax: 'Retenue à la source',
  online: 'Paiement en ligne', other: 'Autre',
};

const PAYMENT_TYPE_LABELS = { invoice: 'Facture', advance: 'Avance', expense: 'Débours' };

function PaymentForm({ onSave, initial, invoices, clients }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      const clientId = initial.clientId
        ? (typeof initial.clientId === 'object' ? initial.clientId._id : initial.clientId) : '';
      return {
        receiptNumber: initial.receiptNumber || '',
        paymentDate: initial.paymentDate ? new Date(initial.paymentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        clientId,
        paymentItems: initial.paymentItems?.map(item => ({
          type: item.type || 'invoice',
          selectedInvoice: item.selectedInvoice ? (typeof item.selectedInvoice === 'object' ? item.selectedInvoice._id : item.selectedInvoice) : '',
          otherObject: item.otherObject || '',
          amount: parseFloat(item.amount) || 0,
          paymentMethod: item.paymentMethod || initial.paymentMethod || 'cash',
          reference: item.reference || '',
          treasuryAccountId: item.treasuryAccountId || initial.treasuryAccountId || '',
          rsRate: item.rsRate || '', rsBase: item.rsBase || '',
        })) || [{ type: 'invoice', selectedInvoice: '', otherObject: '', amount: 0, paymentMethod: 'cash', reference: '', treasuryAccountId: '', rsRate: '', rsBase: '' }],
        totalAmount: parseFloat(initial.totalAmount) || 0,
        notes: initial.notes || '',
      };
    }
    return {
      receiptNumber: '', paymentDate: new Date().toISOString().split('T')[0], clientId: '',
      paymentItems: [{ type: 'invoice', selectedInvoice: '', otherObject: '', amount: 0, paymentMethod: 'cash', reference: '', treasuryAccountId: '', rsRate: '', rsBase: '' }],
      totalAmount: 0, notes: '',
    };
  });

  const [showclientForm, setClientform] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  const filteredClients = clients.filter(client =>
    client.companyName?.toLowerCase().includes(clientSearch.toLowerCase()) ||
    client.contactName?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const initialItemsRef = useRef(
    (initial?.paymentItems || []).map(item => {
      const sel = item.selectedInvoice;
      const id = sel ? (typeof sel === 'object' ? sel._id : sel) : '';
      const number = (typeof sel === 'object' && sel?.number) ? sel.number : '';
      return { type: item.type || 'invoice', selectedInvoiceId: id, amount: parseFloat(item.amount) || 0, invoiceNumber: number };
    })
  );

  const [extraInvoices, setExtraInvoices] = useState([]);

  useEffect(() => {
    if (!initial) return;
    const invoiceIds = Array.from(new Set((initialItemsRef.current || []).filter(it => it.type === 'invoice' && it.selectedInvoiceId).map(it => it.selectedInvoiceId)));
    const missingIds = invoiceIds.filter(id => !invoices.some(inv => inv?._id === id) && !extraInvoices.some(inv => inv?._id === id));
    if (missingIds.length === 0) return;
    let cancelled = false;
    const fetchMissing = async () => {
      const fetched = [];
      for (const id of missingIds) {
        try { const res = await api.get(`/invoices/${id}`); if (res?.data?._id) fetched.push(res.data); } catch (e) {}
      }
      if (cancelled) return;
      if (fetched.length > 0) {
        setExtraInvoices(prev => {
          const map = new Map((prev || []).filter(x => x?._id).map(x => [x._id, x]));
          fetched.forEach(x => { if (x?._id) map.set(x._id, x); });
          return Array.from(map.values());
        });
      }
    };
    fetchMissing();
    return () => { cancelled = true; };
  }, [initial, invoices, extraInvoices]);

  const allInvoices = (() => {
    const map = new Map();
    [...(invoices || []), ...(extraInvoices || [])].forEach(inv => { if (inv && inv._id) map.set(inv._id, inv); });
    return Array.from(map.values());
  })();

  const isInvoiceKnown = (invoiceId) => { if (!invoiceId) return false; return allInvoices.some(inv => inv?._id === invoiceId); };

  const unpaidInvoices = form.clientId
    ? allInvoices.filter(inv => {
        const invoiceClientId = getObjectIdString(inv.clientId);
        const formClientId = getObjectIdString(form.clientId) || form.clientId;
        return (inv.status === 'draft' || inv.status === 'sent' || inv.status === 'partial') && invoiceClientId === formClientId;
      }) : [];

  const invoiceOptions = (() => {
    const map = new Map();
    unpaidInvoices.forEach(inv => { if (inv && inv._id) map.set(inv._id, inv); });
    (initialItemsRef.current || []).forEach(it => {
      if (it.type !== 'invoice' || !it.selectedInvoiceId) return;
      const invId = it.selectedInvoiceId;
      const inv = allInvoices.find(i => i?._id === invId);
      if (inv) map.set(inv._id, inv);
      else map.set(invId, { _id: invId, number: it.invoiceNumber || `Facture (${String(invId).slice(-6)})`, status: 'paid', _missing: true });
    });
    return Array.from(map.values());
  })();

  const getMaxAmount = (invoiceId, currentItemIndex = -1) => {
    if (!invoiceId) return 0;
    const invoice = allInvoices.find(inv => inv._id === invoiceId);
    if (!invoice) return null;
    const total = getInvoiceTotal(invoice);
    const amountPaidInDB = parseFloat(invoice.amountPaid) || 0;
    const amountInOtherItems = form.paymentItems.reduce((sum, item, idx) => {
      if (idx === currentItemIndex || item.type !== 'invoice') return sum;
      if (item.selectedInvoice === invoiceId) return sum + (parseFloat(item.amount) || 0);
      return sum;
    }, 0);
    let remaining = total - amountPaidInDB - amountInOtherItems;
    const originalItem = (initialItemsRef.current || [])[currentItemIndex];
    if (initial && originalItem?.type === 'invoice' && originalItem.selectedInvoiceId === invoiceId) remaining += (parseFloat(originalItem.amount) || 0);
    return Math.max(0, remaining);
  };

  const addPaymentItem = () => {
    setForm({ ...form, paymentItems: [...form.paymentItems, { type: 'invoice', selectedInvoice: '', otherObject: '', amount: 0, paymentMethod: 'cash', reference: '', treasuryAccountId: '', rsRate: '', rsBase: '' }] });
  };

  const removePaymentItem = (index) => {
    if (form.paymentItems.length <= 1) return;
    const newItems = form.paymentItems.filter((_, i) => i !== index);
    const newTotal = newItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    setForm({ ...form, paymentItems: newItems, totalAmount: newTotal });
  };

  const updatePaymentItem = (index, field, value) => {
    const newItems = [...form.paymentItems];
    if (field === 'type') {
      newItems[index] = { type: value, selectedInvoice: '', otherObject: '', amount: 0, paymentMethod: newItems[index].paymentMethod || 'cash', reference: '', treasuryAccountId: newItems[index].treasuryAccountId || '' };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    if (field === 'selectedInvoice' && value && isInvoiceKnown(value)) {
      const maxAmount = getMaxAmount(value, index);
      if (typeof maxAmount === 'number') newItems[index].amount = maxAmount;
    }
    const newTotal = newItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
    setForm({ ...form, paymentItems: newItems, totalAmount: newTotal });
  };

  const handleAmountChange = (index, value) => {
    const amount = parseFloat(value) || 0;
    const item = form.paymentItems[index];
    if (item.type === 'invoice' && item.selectedInvoice && isInvoiceKnown(item.selectedInvoice)) {
      const maxAmount = getMaxAmount(item.selectedInvoice, index);
      if (typeof maxAmount === 'number' && amount > maxAmount) { toastError(`Le montant ne peut pas dépasser ${maxAmount.toFixed(3)} DT pour cette facture`); return; }
    }
    updatePaymentItem(index, 'amount', amount);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.clientId || form.clientId === 'assigned') { toastError('Veuillez sélectionner un client valide'); return; }
    if (form.totalAmount <= 0) { toastError('Le montant total doit être supérieur à 0'); return; }
    for (let i = 0; i < form.paymentItems.length; i++) {
      const item = form.paymentItems[i];
      if (item.type === 'invoice' && !item.selectedInvoice) { toastError(`Veuillez sélectionner une facture pour l'item ${i + 1}`); return; }
      if ((item.type === 'advance' || item.type === 'expense') && (!item.otherObject || item.otherObject.trim() === '')) { toastError(`Veuillez saisir l'objet pour l'item ${i + 1}`); return; }
      if (item.amount <= 0) { toastError(`Le montant doit être supérieur à 0 pour l'item ${i + 1}`); return; }
    }
    onSave({ ...form, treasuryAccountId: form.paymentItems[0]?.treasuryAccountId || form.treasuryAccountId });
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 bg-white p-6 rounded-lg">
        <h3 className="text-base font-semibold border-b pb-2">{initial ? 'Modifier le paiement' : 'Nouveau paiement'}</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Numéro de reçu</label>
          <input type="text" className="w-full border border-gray-300 px-3 py-2 rounded-lg bg-gray-50 text-sm" value={form.receiptNumber} readOnly placeholder="Généré automatiquement" />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date du paiement *</label>
          <input type="date" className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" value={form.paymentDate} onChange={e => setForm({ ...form, paymentDate: e.target.value })} required />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client *</label>
          <div className="flex gap-2 mb-2">
            <input type="text" className="flex-1 border border-gray-300 px-3 py-2 rounded-lg text-sm" placeholder="Rechercher un client..." value={clientSearch} onChange={e => setClientSearch(e.target.value)} />
            <button type="button" className="bg-blue-600 text-white px-3 py-2 rounded-lg hover:bg-blue-700" onClick={() => setClientform(true)}>
              <IconPlus size={16} strokeWidth={1.6} />
            </button>
          </div>
          <select className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" value={form.clientId} onChange={e => setForm({ ...form, clientId: e.target.value })} required>
            <option value="">Sélectionner un client</option>
            {filteredClients.map(client => (
              <option key={client._id} value={client._id}>{client.companyName}{client.contactName ? ` - ${client.contactName}` : ''}</option>
            ))}
          </select>
          {clients.length === 0 && <p className="text-red-500 text-xs mt-1">Aucun client disponible</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Détails du paiement *</label>
          <div className="space-y-4">
            {form.paymentItems.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-sm font-medium text-gray-700">Item {index + 1}</h4>
                  {form.paymentItems.length > 1 && (
                    <button type="button" onClick={() => removePaymentItem(index)} className="text-red-500 hover:text-red-700">
                      <IconX size={16} strokeWidth={1.6} />
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                    <select className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" value={item.type} onChange={e => updatePaymentItem(index, 'type', e.target.value)} required>
                      <option value="invoice">Facture</option>
                      <option value="advance">Avance</option>
                      <option value="expense">Débours</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      {item.type === 'invoice' ? 'Facture à payer' : `Objet ${item.type === 'advance' ? "de l'avance" : 'du débours'}`} *
                    </label>
                    {item.type === 'invoice' ? (
                      <select className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" value={item.selectedInvoice} onChange={e => updatePaymentItem(index, 'selectedInvoice', e.target.value)} required>
                        <option value="">Sélectionner une facture</option>
                        {invoiceOptions.map(invoice => {
                          if (invoice._missing) return <option key={invoice._id} value={invoice._id}>{invoice.number} - (facture non chargée)</option>;
                          const total = getInvoiceTotal(invoice);
                          const remaining = total - (parseFloat(invoice.amountPaid) || 0);
                          return <option key={invoice._id} value={invoice._id}>{invoice.number} - {Math.max(0, remaining).toFixed(3)} DT restant</option>;
                        })}
                      </select>
                    ) : (
                      <input type="text" className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" placeholder={`Saisir l'objet`} value={item.otherObject} onChange={e => updatePaymentItem(index, 'otherObject', e.target.value)} required />
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Montant (DT) *</label>
                    <div className="relative">
                      <input type="text" inputMode="decimal"
                        className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm pl-10"
                        value={item.amount === 0 && item._amountEditing ? '' : item.amount}
                        onChange={e => { const raw = e.target.value.replace(',', '.'); if (raw === '' || raw === '.') { updatePaymentItem(index, 'amount', 0); return; } if (/^\d*\.?\d*$/.test(raw)) handleAmountChange(index, raw); }}
                        placeholder="0.000" required />
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">DT</span>
                    </div>
                    {item.type === 'invoice' && item.selectedInvoice && isInvoiceKnown(item.selectedInvoice) && (
                      <p className="text-xs text-gray-500 mt-1">Restant: <strong>{(getMaxAmount(item.selectedInvoice, index) || 0).toFixed(3)} DT</strong>
                        {parseFloat(item.amount) > 0 && parseFloat(item.amount) < (getMaxAmount(item.selectedInvoice, index) || 0) && <span className="text-orange-500 ml-1">(Partiel)</span>}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Mode de paiement *</label>
                    <select className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" value={item.paymentMethod || 'cash'} onChange={e => updatePaymentItem(index, 'paymentMethod', e.target.value)} required>
                      <option value="cash">Espèces</option>
                      <option value="check">Chèque</option>
                      <option value="bank_transfer">Virement</option>
                      <option value="draft">Traite</option>
                      <option value="withholding_tax">Retenue à la source (RS)</option>
                      <option value="online">Paiement en ligne</option>
                      <option value="other">Autre</option>
                    </select>
                  </div>
                  {item.paymentMethod !== 'withholding_tax' && (
                    <div>
                      <TreasuryAccountSelector paymentMethod={item.paymentMethod} selectedAccountId={item.treasuryAccountId} onChange={(accountId) => updatePaymentItem(index, 'treasuryAccountId', accountId)} />
                    </div>
                  )}
                  {item.paymentMethod === 'withholding_tax' && (
                    <div className="md:col-span-2">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-3">
                        <div className="flex items-center gap-2 mb-1">
                          <IconPercentage size={16} strokeWidth={1.6} className="text-yellow-600" />
                          <span className="text-sm font-semibold text-yellow-800">Calcul Retenue à la Source</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Taux RS *</label>
                            <select className="w-full border border-gray-300 px-2 py-1.5 rounded-lg text-sm" value={item.rsRate || ''}
                              onChange={e => {
                                const rate = e.target.value;
                                const base = parseFloat(String(item.rsBase || '0').replace(',', '.')) || 0;
                                const rsAmount = base > 0 && rate ? parseFloat((base * parseFloat(rate) / 100).toFixed(3)) : parseFloat(item.amount) || 0;
                                const newItems = [...form.paymentItems];
                                newItems[index] = { ...newItems[index], rsRate: rate, amount: rsAmount };
                                setForm({ ...form, paymentItems: newItems, totalAmount: newItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0) });
                              }}>
                              <option value="">-- Choisir --</option>
                              <option value="1.5">1,5% (Honoraires)</option>
                              <option value="3">3% (Standard)</option>
                              <option value="5">5% (Renforcé)</option>
                              <option value="10">10% (Dividendes)</option>
                              <option value="15">15% (Non-résidents)</option>
                              <option value="25">25% (Anonymes)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Montant base HT</label>
                            <input type="text" inputMode="decimal" className="w-full border border-gray-300 px-2 py-1.5 rounded-lg text-sm" placeholder="Montant HT..." value={item.rsBase || ''}
                              onChange={e => {
                                const raw = e.target.value.replace(',', '.');
                                if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
                                const base = parseFloat(raw) || 0;
                                const rate = parseFloat(item.rsRate) || 0;
                                const rsAmount = base > 0 && rate ? parseFloat((base * rate / 100).toFixed(3)) : parseFloat(item.amount) || 0;
                                const newItems = [...form.paymentItems];
                                newItems[index] = { ...newItems[index], rsBase: e.target.value.replace(',', '.'), amount: rsAmount };
                                setForm({ ...form, paymentItems: newItems, totalAmount: newItems.reduce((s, it) => s + (parseFloat(it.amount) || 0), 0) });
                              }} />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">Montant RS calculé</label>
                            <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white text-sm font-bold text-yellow-700">
                              {item.rsRate && item.rsBase ? (parseFloat(String(item.rsBase).replace(',', '.')) * parseFloat(item.rsRate) / 100).toFixed(3) : (parseFloat(item.amount) || 0).toFixed(3)} DT
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {['check', 'withholding_tax', 'draft', 'bank_transfer'].includes(item.paymentMethod) && (
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        {item.paymentMethod === 'check' ? 'Numéro de Chèque' : item.paymentMethod === 'withholding_tax' ? 'Numéro Certificat RS' : item.paymentMethod === 'draft' ? 'Numéro de Traite' : 'Référence Virement'}
                      </label>
                      <input type="text" className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm" value={item.reference || ''} onChange={e => updatePaymentItem(index, 'reference', e.target.value)} placeholder="Ex: 12345678" />
                    </div>
                  )}
                </div>
              </div>
            ))}
            <button type="button" onClick={addPaymentItem} className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 flex items-center justify-center gap-2 transition-colors">
              <IconPlus size={16} strokeWidth={1.6} />
              Ajouter un item de paiement
            </button>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 p-4 rounded-xl">
          <div className="flex justify-between items-center">
            <span className="font-semibold text-gray-700">Total</span>
            <span className="text-2xl font-bold text-blue-700">{form.totalAmount.toFixed(3)} DT</span>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea className="w-full border border-gray-300 px-3 py-2 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes supplémentaires..." rows="3" />
        </div>

        <div className="flex gap-3 pt-4 border-t">
          <Button type="submit" variant="success" className="flex-1">{initial ? 'Modifier' : 'Valider le paiement'}</Button>
          <Button type="button" variant="secondary" className="flex-1" onClick={() => onSave(null)}>Annuler</Button>
        </div>
      </form>

      {showclientForm && <ClientForm onSave={() => setClientform(false)} onCancel={() => setClientform(false)} />}
    </>
  );
}

PaymentForm.propTypes = { onSave: PropTypes.func.isRequired, initial: PropTypes.object, invoices: PropTypes.array.isRequired, clients: PropTypes.array.isRequired };

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients]   = useState([]);
  const [editing, setEditing]   = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [filters, setFilters]   = useState({ dateDebut: '', dateFin: '', client: '', paymentType: '', minAmount: '', maxAmount: '' });
  const [cabinetInfo, setCabinetInfo] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const getPaymentClientName = (payment) => {
    if (!payment.clientId) return 'N/A';
    if (typeof payment.clientId === 'object' && payment.clientId.companyName) return payment.clientId.companyName;
    const client = clients.find(c => c._id === getObjectIdString(payment.clientId));
    return client?.companyName || 'N/A';
  };

  const paymentExportColumns = [
    { key: 'date', label: 'Date', accessor: (p) => p.paymentDate ? new Date(p.paymentDate).toLocaleDateString('fr-FR') : '' },
    { key: 'receiptNumber', label: 'Numéro Reçu', accessor: (p) => p.receiptNumber || '' },
    { key: 'client', label: 'Client', accessor: getPaymentClientName },
    { key: 'totalAmount', label: 'Total (DT)', accessor: (p) => parseFloat(p.totalAmount || 0).toFixed(3) },
    { key: 'paymentMethod', label: 'Mode', accessor: (p) => PAYMENT_METHOD_LABELS[p.paymentMethod] || p.paymentMethod },
    { key: 'reference', label: 'Référence', accessor: (p) => p.reference || '' },
  ];

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  const { canAccessClient, canModifyResource, canDeleteResource, hasPermission } = usePermissions();

  const getLogoUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http') || path.startsWith('blob:') || path.startsWith('data:')) return path;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
    return `${API_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  };

  const chargerCabinetInfo = async () => {
    try {
      const response = await api.get('/settings');
      const data = response.data;
      setCabinetInfo({ name: data.companyName || data.name || 'CABINET', address: data.address || '', city: data.city || '', phone: data.phone || '', email: data.email || '', taxId: data.fiscalId || data.taxId || '', rib: data.rib || '', bank: data.bank || '', paymentTerms: data.paymentTerms || 'Paiement à 30 jours', defaultNotes: data.defaultNotes || '', footerText: data.footerText || 'Merci de votre confiance', logo: data.logo || '', ...data });
    } catch (error) { console.error('Erreur chargement infos cabinet:', error); }
  };

  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const [paymentsRes, invoicesRes, clientsRes] = await Promise.all([api.get('/payments'), api.get('/invoices'), api.get('/clients')]);
        if (!isMounted) return;
        setPayments(paymentsRes.data || []); setInvoices(invoicesRes.data || []); setClients(clientsRes.data || []);
        await chargerCabinetInfo();
      } catch (error) { console.error('Erreur chargement données:', error); }
      finally { if (isMounted) setLoading(false); }
    };
    fetchData();
    return () => { isMounted = false; };
  }, []);

  const reloadData = async () => {
    try {
      const [paymentsRes, invoicesRes, clientsRes] = await Promise.all([api.get('/payments'), api.get('/invoices'), api.get('/clients')]);
      setPayments(paymentsRes.data || []); setInvoices(invoicesRes.data || []); setClients(clientsRes.data || []);
      await chargerCabinetInfo();
    } catch (error) { console.error('Erreur rechargement:', error); }
  };

  const savePayment = async (data) => {
    if (data === null) { setShowForm(false); setEditing(null); return; }
    if (editing && !canModifyResource(editing, 'payment')) { toastError("Vous n'êtes pas autorisé à modifier ce paiement"); return; }
    if (!data.clientId || data.clientId === 'assigned') { toastError('Veuillez sélectionner un client valide'); return; }
    if (!canAccessClient(data.clientId)) { toastError("Vous n'avez pas accès à ce client"); return; }
    try {
      const paymentItemsToSave = data.paymentItems.map(item => {
        const itemToSave = { type: item.type, amount: parseFloat(item.amount), paymentMethod: item.paymentMethod || 'cash', reference: item.reference || '', treasuryAccountId: item.treasuryAccountId || '' };
        if (item.type === 'invoice' && item.selectedInvoice) itemToSave.selectedInvoice = typeof item.selectedInvoice === 'object' ? item.selectedInvoice._id : item.selectedInvoice;
        else if (item.type !== 'invoice' && item.otherObject) itemToSave.otherObject = item.otherObject;
        return itemToSave;
      });
      const uniqueMethods = [...new Set(paymentItemsToSave.map(i => i.paymentMethod))];
      const primaryPaymentMethod = uniqueMethods.find(m => m !== 'withholding_tax') || uniqueMethods[0] || 'cash';
      const paymentData = { paymentDate: new Date(data.paymentDate).toISOString(), clientId: data.clientId, paymentItems: paymentItemsToSave, totalAmount: parseFloat(data.totalAmount), paymentMethod: primaryPaymentMethod, notes: data.notes || '', status: 'completed', treasuryAccountId: data.paymentItems[0]?.treasuryAccountId || data.treasuryAccountId };
      if (editing) { if (editing.receiptNumber) paymentData.receiptNumber = editing.receiptNumber; await api.put(`/payments/${editing._id}`, paymentData); }
      else await api.post('/payments', paymentData);
      setShowForm(false); setEditing(null);
      await reloadData();
      window.dispatchEvent(new CustomEvent('paymentUpdated'));
      toastSuccess(editing ? 'Paiement modifié avec succès' : 'Paiement enregistré avec succès');
    } catch (err) { toastError('Erreur lors de la sauvegarde: ' + (err.response?.data?.error || err.response?.data?.message || err.message)); }
  };

  const deletePayment = async (id) => {
    const payment = payments.find(p => p._id === id);
    if (!payment) { toastError('Paiement non trouvé'); return; }
    if (!canDeleteResource(payment, 'payment')) { toastError("Vous n'êtes pas autorisé à supprimer ce paiement"); return; }
    const clientId = getObjectIdString(payment.clientId);
    if (!canAccessClient(clientId)) { toastError("Vous n'avez pas accès à ce paiement"); return; }
    if (!await showConfirm('Êtes-vous sûr de vouloir supprimer ce paiement ?')) return;
    try {
      await api.delete(`/payments/${id}`);
      await reloadData();
      window.dispatchEvent(new CustomEvent('paymentUpdated'));
      toastSuccess('Paiement supprimé avec succès');
    } catch (err) { toastError('Erreur lors de la suppression: ' + (err.response?.data?.error || err.message)); }
  };

  const imprimerRecu = (payment) => {
    const clientId = getObjectIdString(payment.clientId);
    if (!canAccessClient(clientId)) { toastError("Vous n'avez pas accès à ce paiement"); return; }
    const printWindow = window.open('', '_blank');
    let clientName = 'N/A';
    if (payment.clientId) {
      if (typeof payment.clientId === 'object' && payment.clientId.companyName) clientName = payment.clientId.companyName;
      else { const foundClient = clients.find(c => c._id === getObjectIdString(payment.clientId)); clientName = foundClient?.companyName || 'N/A'; }
    }
    const cab = cabinetInfo || {};
    const logoSrc = getLogoUrl(cab.logo);
    const content = `<!DOCTYPE html><html><head><title>Reçu - ${payment.receiptNumber || 'N/A'}</title><style>body{font-family:Arial,sans-serif;margin:40px;line-height:1.6}.header{text-align:center;margin-bottom:30px;border-bottom:2px solid #333;padding-bottom:20px}table{width:100%;border-collapse:collapse;margin:25px 0}th,td{border:1px solid #ddd;padding:10px;text-align:left}th{background:#f8f9fa;font-weight:bold}.total{font-size:18px;font-weight:bold;text-align:right;margin-top:20px;padding:15px;background:#f8f9fa}.footer{margin-top:40px;text-align:center;font-size:12px;color:#666;border-top:1px solid #ddd;padding-top:15px}</style></head><body><div class="header">${logoSrc ? `<img src="${logoSrc}" alt="Logo" style="max-height:70px;max-width:200px;object-fit:contain;margin-bottom:10px;"/>` : ''}<h1>REÇU DE PAIEMENT</h1><p><strong>${cab.companyName || cab.name || 'Cabinet Comptable'}</strong></p>${cab.fiscalId || cab.taxId ? `<p>MF: ${cab.fiscalId || cab.taxId}</p>` : ''}${cab.address ? `<p>${cab.address}</p>` : ''}<p>Reçu N°: <strong>${payment.receiptNumber || 'N/A'}</strong></p></div><p><strong>Date:</strong> ${payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('fr-FR') : ''}</p><p><strong>Client:</strong> ${clientName}</p><table><thead><tr><th>Description</th><th>Type</th><th>Mode</th><th>Référence</th><th style="text-align:right">Montant (DT)</th></tr></thead><tbody>${(payment.paymentItems || []).map(item => { const invoiceNum = getInvoiceNumber(item.selectedInvoice, invoices); const description = item.type === 'invoice' ? `Facture ${invoiceNum}` : item.otherObject || 'N/A'; const method = item.paymentMethod || payment.paymentMethod || 'cash'; return `<tr><td>${description}</td><td>${PAYMENT_TYPE_LABELS[item.type] || item.type}</td><td>${PAYMENT_METHOD_LABELS[method] || method}</td><td>${item.reference || '-'}</td><td style="text-align:right">${parseFloat(item.amount).toFixed(3)}</td></tr>`; }).join('')}</tbody></table><div class="total">TOTAL: ${parseFloat(payment.totalAmount || 0).toFixed(3)} DT</div>${payment.notes ? `<p><strong>Notes:</strong> ${payment.notes}</p>` : ''}<div class="footer"><p>Reçu généré le ${new Date().toLocaleDateString('fr-FR')} - ${cab.footerText || 'Merci pour votre confiance'}</p></div></body></html>`;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => { printWindow.focus(); setTimeout(() => printWindow.print(), 300); };
  };

  const clientsAccessibles = clients.filter(client => client && (client._id || client.id) && canAccessClient(client._id || client.id));
  const paymentsAccessibles = payments.filter(payment => { const clientId = getObjectIdString(payment.clientId); return clientId ? canAccessClient(clientId) : false; });
  const filteredPayments = paymentsAccessibles.filter(payment => {
    const paymentDate = new Date(payment.paymentDate);
    if (filters.dateDebut && paymentDate < new Date(filters.dateDebut)) return false;
    if (filters.dateFin && paymentDate > new Date(filters.dateFin)) return false;
    if (filters.client && filters.client !== 'assigned' && getObjectIdString(payment.clientId) !== filters.client) return false;
    if (filters.paymentType && !payment.paymentItems?.some(item => item.type === filters.paymentType)) return false;
    if (filters.minAmount && parseFloat(payment.totalAmount) < parseFloat(filters.minAmount)) return false;
    if (filters.maxAmount && parseFloat(payment.totalAmount) > parseFloat(filters.maxAmount)) return false;
    return true;
  });

  useEffect(() => { setPage(1); }, [filteredPayments.length]);

  const totalPages = Math.ceil(filteredPayments.length / PAGE_SIZE) || 1;
  const paginatedPayments = filteredPayments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const totalReceived = filteredPayments.reduce((sum, p) => sum + parseFloat(p.totalAmount || 0), 0);
  const canCreatePayment = hasPermission('payments', 'create');

  if (loading) return (
    <div className="p-6 flex items-center justify-center h-64">
      <div className="text-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
        <p className="text-gray-500 text-sm">Chargement des paiements...</p>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 mb-1">Paiements</h1>
          <p className="text-gray-500 text-sm">Gestion des règlements clients</p>
        </div>
        {canCreatePayment && (
          <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setEditing(null); }}>
            <IconPlus size={15} strokeWidth={1.6} />
            Nouveau paiement
          </Button>
        )}
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total perçu', value: `${totalReceived.toFixed(3)} DT`, icon: <IconCurrencyDollar size={20} strokeWidth={1.5} className="text-green-600" />, bg: 'bg-green-50' },
          { label: 'Paiements factures', value: paymentsAccessibles.filter(p => p.paymentItems?.some(i => i.type === 'invoice')).length, icon: <IconFileInvoice size={20} strokeWidth={1.5} className="text-blue-600" />, bg: 'bg-blue-50' },
          { label: 'Avances', value: paymentsAccessibles.filter(p => p.paymentItems?.some(i => i.type === 'advance')).length, icon: <IconHandClick size={20} strokeWidth={1.5} className="text-purple-600" />, bg: 'bg-purple-50' },
          { label: 'Total paiements', value: paymentsAccessibles.length, icon: <IconReceipt size={20} strokeWidth={1.5} className="text-orange-600" />, bg: 'bg-orange-50' },
        ].map(({ label, value, icon, bg }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
            <div className={`${bg} p-2.5 rounded-lg flex-shrink-0`}>{icon}</div>
            <div>
              <p className="text-xs text-gray-500">{label}</p>
              <p className="text-xl font-bold text-gray-800">{value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4">
          {[
            { label: 'Date début', key: 'dateDebut', type: 'date' },
            { label: 'Date fin', key: 'dateFin', type: 'date' },
          ].map(({ label, key, type }) => (
            <div key={key}>
              <label className="block text-xs font-medium mb-1 text-gray-600">{label}</label>
              <input type={type} value={filters[key]} onChange={e => { setFilters({ ...filters, [key]: e.target.value }); setPage(1); }} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
            </div>
          ))}
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Client</label>
            <select value={filters.client} onChange={e => { setFilters({ ...filters, client: e.target.value }); setPage(1); }} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
              <option value="">Tous les clients</option>
              {clientsAccessibles.map(client => <option key={client._id} value={client._id}>{client.companyName}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Type</label>
            <select value={filters.paymentType} onChange={e => { setFilters({ ...filters, paymentType: e.target.value }); setPage(1); }} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm">
              <option value="">Tous</option>
              <option value="invoice">Facture</option>
              <option value="advance">Avance</option>
              <option value="expense">Débours</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Montant min</label>
            <input type="number" placeholder="0" value={filters.minAmount} onChange={e => { setFilters({ ...filters, minAmount: e.target.value }); setPage(1); }} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1 text-gray-600">Montant max</label>
            <input type="number" placeholder="Max" value={filters.maxAmount} onChange={e => { setFilters({ ...filters, maxAmount: e.target.value }); setPage(1); }} className="w-full px-2 py-1.5 border border-gray-300 rounded-lg text-sm" />
          </div>
        </div>
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">{filteredPayments.length} paiement(s) trouvé(s)</p>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => { setFilters({ dateDebut: '', dateFin: '', client: '', paymentType: '', minAmount: '', maxAmount: '' }); setPage(1); }}>
              <IconX size={14} strokeWidth={1.6} />
              Réinitialiser
            </Button>
            {hasPermission('payments', 'export') && (
              <Button variant="info" size="sm" onClick={() => setShowExportModal(true)}>
                <IconFileExport size={14} strokeWidth={1.6} />
                Exporter
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {['Date', 'Reçu', 'Client', 'Items', 'Total', 'Mode', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {paginatedPayments.map(payment => {
                const canModify = canModifyResource(payment, 'payment');
                const canDelete = canDeleteResource(payment, 'payment');
                let clientName = 'N/A';
                if (payment.clientId) {
                  if (typeof payment.clientId === 'object' && payment.clientId.companyName) clientName = payment.clientId.companyName;
                  else { const fc = clients.find(c => c._id === getObjectIdString(payment.clientId)); clientName = fc?.companyName || 'N/A'; }
                }
                return (
                  <tr key={payment._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{payment.paymentDate ? new Date(payment.paymentDate).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-gray-800 whitespace-nowrap">{payment.receiptNumber || '—'}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 whitespace-nowrap">{clientName}</td>
                    <td className="px-4 py-3 text-sm text-gray-800">
                      <div className="space-y-1">
                        {payment.paymentItems?.map((item, index) => {
                          const invoiceNum = getInvoiceNumber(item.selectedInvoice, invoices);
                          const description = item.type === 'invoice' ? `Facture ${invoiceNum}` : item.otherObject || 'N/A';
                          const itemMethod = item.paymentMethod || payment.paymentMethod;
                          return (
                            <div key={index} className="flex items-center gap-2 flex-wrap">
                              <Badge variant={item.type === 'invoice' ? 'info' : item.type === 'advance' ? 'gray' : 'warning'}>
                                {PAYMENT_TYPE_LABELS[item.type] || item.type}
                              </Badge>
                              <span className="text-xs text-gray-700">{description}</span>
                              <span className="text-xs font-semibold text-gray-900">{parseFloat(item.amount).toFixed(3)} DT</span>
                              <span className="text-xs text-gray-400">({PAYMENT_METHOD_LABELS[itemMethod] || itemMethod})</span>
                            </div>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900 whitespace-nowrap">{parseFloat(payment.totalAmount || 0).toFixed(3)} DT</td>
                    <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">
                      {[...new Set(payment.paymentItems?.map(i => i.paymentMethod || payment.paymentMethod) || [payment.paymentMethod])].map(m => PAYMENT_METHOD_LABELS[m] || m).join(', ')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => imprimerRecu(payment)} title="Imprimer le reçu" className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 border border-blue-200 transition-colors">
                          <IconPrinter size={14} strokeWidth={1.6} />
                        </button>
                        {canModify && (
                          <button onClick={() => { setEditing(payment); setShowForm(true); }} title="Modifier" className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 border border-amber-200 transition-colors">
                            <IconEdit size={14} strokeWidth={1.6} />
                          </button>
                        )}
                        {canDelete && (
                          <button onClick={() => deletePayment(payment._id)} title="Supprimer" className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 border border-red-200 transition-colors">
                            <IconTrash size={14} strokeWidth={1.6} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {filteredPayments.length === 0 && (
          <div className="text-center py-12">
            <IconReceipt size={40} strokeWidth={1.2} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">Aucun paiement trouvé</p>
          </div>
        )}
        {!loading && filteredPayments.length > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-xs text-gray-500">
              {filteredPayments.length} paiement(s) — Page {page}/{totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronLeft size={16} strokeWidth={1.6} />
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const start = Math.max(1, Math.min(page - 2, totalPages - 4));
                const p = start + i;
                if (p > totalPages) return null;
                return (
                  <button key={p} onClick={() => setPage(p)}
                    className={`min-w-[32px] px-2 py-1 text-xs font-medium rounded-lg border transition-colors ${p === page ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-200 text-gray-600 hover:bg-white'}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                <IconChevronRight size={16} strokeWidth={1.6} />
              </button>
            </div>
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <PaymentForm onSave={savePayment} initial={editing} invoices={invoices} clients={clientsAccessibles} />
          </div>
        </div>
      )}

      {showExportModal && (
        <ExportModal
          show={showExportModal}
          onClose={() => setShowExportModal(false)}
          title="Exporter les paiements"
          columns={paymentExportColumns}
          data={filteredPayments}
          filename="paiements"
        />
      )}
    </div>
  );
}
